import React, { useEffect, useState, useCallback, useRef } from 'react';
import { host } from '../script/variables';
import AdminBlogPreview from './AdminBlogPreview';
import '../style/AdminBlogsList.css';

const PAGE_LIMIT = 10;

const TABS = [
  { key: 'pending',  label: 'Pending Approval', icon: '⏳' },
  { key: 'approved', label: 'Approved',          icon: '✅' },
];

const timeAgo = (timestamp) => {
  const diff   = Date.now() - new Date(timestamp);
  const mins   = Math.floor(diff / 60000);
  const hours  = Math.floor(mins  / 60);
  const days   = Math.floor(hours / 24);
  const weeks  = Math.floor(days  / 7);
  const months = Math.floor(days  / 30);
  if (months > 0) return `${months}mo ago`;
  if (weeks  > 0) return `${weeks}w ago`;
  if (days   > 0) return `${days}d ago`;
  if (hours  > 0) return `${hours}h ago`;
  if (mins   > 0) return `${mins}m ago`;
  return 'just now';
};

const readingTime = (body = []) => {
  const words = body.reduce((acc, block) => {
    const text = block.data?.text || (block.data?.items || []).join(' ');
    return acc + text.split(/\s+/).filter(Boolean).length;
  }, 0);
  return Math.max(1, Math.ceil(words / 200));
};

/* ─── Skeleton Card ─────────────────────────────────────── */
const SkeletonCard = () => (
  <div className="bl-card bl-card--skeleton">
    <div className="bl-card__thumb bl-skel" />
    <div className="bl-card__body">
      <div className="bl-skel bl-skel--tag" />
      <div className="bl-skel bl-skel--title" />
      <div className="bl-skel bl-skel--title bl-skel--short" />
      <div className="bl-skel bl-skel--meta" />
    </div>
  </div>
);

/* ─── Blog Card ─────────────────────────────────────────── */
const BlogCard = ({ blog, onSelect, onApprove, onRevoke, tab }) => {
  const [acting, setActing] = useState(false);
  const mins = readingTime(blog.body || []);

  const handleAction = async (e, action) => {
    e.stopPropagation();
    setActing(true);
    try { await action(); } finally { setActing(false); }
  };

  return (
    <article
      className="bl-card"
      onClick={() => onSelect(blog)}
      tabIndex={0}
      onKeyDown={e => e.key === 'Enter' && onSelect(blog)}
    >
      <div className="bl-card__thumb-wrap">
        {blog.thumbnail && blog.thumbnail !== '#' ? (
          <img src={blog.thumbnail} alt={blog.title} className="bl-card__thumb" loading="lazy" />
        ) : (
          <div className="bl-card__thumb bl-card__thumb--placeholder"><span>📝</span></div>
        )}
        <span className={`bl-badge bl-badge--${blog.status}`}>{blog.status}</span>
      </div>

      <div className="bl-card__body">
        {blog.tags?.length > 0 && (
          <div className="bl-card__tags">
            {blog.tags.slice(0, 3).map(t => (
              <span className="bl-card__tag" key={t}>#{t}</span>
            ))}
          </div>
        )}
        <h3 className="bl-card__title">{blog.title}</h3>
        <div className="bl-card__meta">
          <img
            src={blog.authorDetails?.image || '/default-avatar.png'}
            alt={blog.authorDetails?.username}
            className="bl-card__avatar"
          />
          <span className="bl-card__author">{blog.authorDetails?.username || 'Unknown'}</span>
          <span className="bl-dot">·</span>
          <span>{timeAgo(blog.createdAt)}</span>
          <span className="bl-dot">·</span>
          <span>{mins} min read</span>
        </div>

        <div className="bl-card__actions">
          <button className="bl-btn bl-btn--preview" onClick={e => { e.stopPropagation(); onSelect(blog); }}>
            Preview
          </button>
          {tab === 'pending' && (
            <button
              className="bl-btn bl-btn--approve"
              onClick={e => handleAction(e, () => onApprove(blog._id))}
              disabled={acting}
            >
              {acting ? '...' : 'Approve'}
            </button>
          )}
          {tab === 'approved' && (
            <button
              className="bl-btn bl-btn--revoke"
              onClick={e => handleAction(e, () => onRevoke(blog._id))}
              disabled={acting}
            >
              {acting ? '...' : 'Revoke'}
            </button>
          )}
        </div>
      </div>
    </article>
  );
};

/* ════════════════════════════════════════════════════════════
   MAIN COMPONENT
════════════════════════════════════════════════════════════ */
const BlogsList = () => {
  const [tab,          setTab]          = useState('pending');
  const [blogs,        setBlogs]        = useState([]);
  const [page,         setPage]         = useState(1);
  const [hasMore,      setHasMore]      = useState(true);
  const [loading,      setLoading]      = useState(true);      // first-page load
  const [loadingMore,  setLoadingMore]  = useState(false);     // subsequent pages
  const [error,        setError]        = useState('');
  const [selectedBlog, setSelectedBlog] = useState(null);
  const [search,       setSearch]       = useState('');
  const [toast,        setToast]        = useState(null);

  // sentinel ref for IntersectionObserver
  const sentinelRef = useRef(null);

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  };

  /* ── Fetch a specific page ───────────────────────────── */
  const fetchPage = useCallback(async (pageNum, replace = false) => {
    replace ? setLoading(true) : setLoadingMore(true);
    setError('');

    try {
      const endpoint = tab === 'pending'
        ? `${host}/admin/blogs/pending`
        : `${host}/admin/blogs/approved`;

      const res = await fetch(
        `${endpoint}?page=${pageNum}&limit=${PAGE_LIMIT}`,
        { method: 'GET', credentials: 'include' }
      );

      if (!res.ok) throw new Error('Failed to fetch blogs');

      const data = await res.json();           // { blogs, hasMore, total, page }

      setBlogs(prev => replace ? data.blogs : [...prev, ...data.blogs]);
      setHasMore(data.hasMore);
      setPage(pageNum);
    } catch (e) {
      setError(e.message || 'Error loading blogs');
    } finally {
      replace ? setLoading(false) : setLoadingMore(false);
    }
  }, [tab]);

  /* ── Reset & reload when tab changes ────────────────── */
  useEffect(() => {
    setBlogs([]);
    setPage(1);
    setHasMore(true);
    setSearch('');
    fetchPage(1, true);
  }, [tab]);                                   // eslint-disable-line react-hooks/exhaustive-deps

  /* ── IntersectionObserver ────────────────────────────── */
  useEffect(() => {
    if (!sentinelRef.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry.isIntersecting && hasMore && !loadingMore && !loading) {
          fetchPage(page + 1, false);
        }
      },
      { rootMargin: '200px' }                  // trigger 200 px before sentinel is visible
    );

    observer.observe(sentinelRef.current);
    return () => observer.disconnect();
  }, [hasMore, loadingMore, loading, page, fetchPage]);

  /* ── Approve / Revoke ────────────────────────────────── */
  const handleApprove = async (blogId) => {
    try {
      const res = await fetch(`${host}/admin/blogs/${blogId}/approve`, {
        method: 'PATCH',
        credentials: 'include',
      });
      if (!res.ok) throw new Error('Approval failed');
      setBlogs(prev => prev.filter(b => b._id !== blogId));
      if (selectedBlog?._id === blogId) setSelectedBlog(prev => ({ ...prev, isApproved: true }));
      showToast('Blog approved successfully!');
    } catch (e) {
      showToast(e.message, 'error');
    }
  };

  const handleRevoke = async (blogId) => {
    try {
      const res = await fetch(`${host}/admin/blogs/${blogId}/revoke`, {
        method: 'PATCH',
        credentials: 'include',
      });
      if (!res.ok) throw new Error('Revoke failed');
      setBlogs(prev => prev.filter(b => b._id !== blogId));
      if (selectedBlog?._id === blogId) setSelectedBlog(prev => ({ ...prev, isApproved: false }));
      showToast('Blog approval revoked.');
    } catch (e) {
      showToast(e.message, 'error');
    }
  };

  /* ── Client-side search filter ───────────────────────── */
  const filtered = blogs.filter(b =>
    b.title?.toLowerCase().includes(search.toLowerCase()) ||
    b.authorDetails?.username?.toLowerCase().includes(search.toLowerCase()) ||
    b.tags?.some(t => t.toLowerCase().includes(search.toLowerCase()))
  );

  /* ── Render ──────────────────────────────────────────── */
  return (
    <div className="bl-root">
      {/* Toast */}
      {toast && (
        <div className={`bl-toast bl-toast--${toast.type}`}>
          {toast.type === 'success' ? '✓' : '✕'} {toast.msg}
        </div>
      )}

      {/* Preview Drawer */}
      {selectedBlog && (
        <AdminBlogPreview
          blog={selectedBlog}
          onClose={() => setSelectedBlog(null)}
          onApprove={handleApprove}
          onRevoke={handleRevoke}
        />
      )}

      <div className="bl-header">
        {/* <div className="bl-header__top">
          <h1 className="bl-header__title">Blog Management</h1>
          <div className="bl-header__count">
            {!loading && (
              <span className="bl-count-chip">
                {filtered.length}{hasMore ? '+' : ''} {tab === 'pending' ? 'pending' : 'approved'}
              </span>
            )}
          </div>
        </div> */}

        {/* Tabs */}
        <div className="bl-tabs">
          {TABS.map(t => (
            <button
              key={t.key}
              className={`bl-tab${tab === t.key ? ' bl-tab--active' : ''}`}
              onClick={() => setTab(t.key)}
            >
              <span className="bl-tab__icon">{t.icon}</span>
              {t.label}
            </button>
          ))}
        </div>

        {/* Search */}
        <div className="bl-search-wrap">
          <span className="bl-search__icon">🔍</span>
          <input
            className="bl-search"
            type="text"
            placeholder="Search by title, author, or tag..."
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          {search && (
            <button className="bl-search__clear" onClick={() => setSearch('')}>✕</button>
          )}
        </div>
      </div>

      <div className="bl-body">
        {error && (
          <div className="bl-error">
            <span>⚠️</span> {error}
            <button className="bl-btn bl-btn--retry" onClick={() => fetchPage(page, false)}>Retry</button>
          </div>
        )}

        {/* Initial skeleton */}
        {loading ? (
          <div className="bl-grid">
            {[...Array(6)].map((_, i) => <SkeletonCard key={i} />)}
          </div>
        ) : filtered.length === 0 ? (
          <div className="bl-empty">
            <div className="bl-empty__icon">{tab === 'pending' ? '🎉' : '📭'}</div>
            <h3 className="bl-empty__title">
              {search
                ? 'No blogs match your search'
                : tab === 'pending' ? 'All caught up!' : 'No approved blogs yet'}
            </h3>
            <p className="bl-empty__sub">
              {search
                ? 'Try a different keyword.'
                : tab === 'pending'
                  ? 'No blogs are awaiting approval.'
                  : 'Approve some blogs to see them here.'}
            </p>
          </div>
        ) : (
          <>
            <div className="bl-grid">
              {filtered.map(blog => (
                <BlogCard
                  key={blog._id}
                  blog={blog}
                  tab={tab}
                  onSelect={setSelectedBlog}
                  onApprove={handleApprove}
                  onRevoke={handleRevoke}
                />
              ))}
            </div>

            {/* Skeleton rows while loading more */}
            {loadingMore && (
              <div className="bl-grid bl-grid--more">
                {[...Array(3)].map((_, i) => <SkeletonCard key={i} />)}
              </div>
            )}

            {/* Invisible sentinel — observer watches this */}
            <div ref={sentinelRef} className="bl-sentinel" aria-hidden="true" />

            {/* End-of-list message */}
            {!hasMore && !loadingMore && (
              <p className="bl-end-msg">You've reached the end · {blogs.length} blogs loaded</p>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default BlogsList;